import type { RegisterOrganizationPayload } from '@/services/onboardingService';
import { slugify } from '@/utils/slug';
import {
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  DRAFT_STORAGE_KEY,
  EMAIL_PATTERN,
  PHONE_PATTERN,
  URL_PATTERN,
} from './constants';
import type { AuthUserInfo, FormState, StepErrors } from './types';

export const EMPTY_FORM: FormState = {
  orgName: '',
  sector: '',
  industry: '',
  organizationSize: '',
  website: '',
  logo: null,
  logoPreview: '',
  logoUrl: '',
  country: 'Ethiopia',
  city: '',
  address: '',
  phoneDial: '+251',
  phoneNumber: '',
  contactPerson: '',
  contactRole: '',
  alternateContactName: '',
  alternateContactRole: '',
  alternateContactEmail: '',
  description: '',
  acknowledgeAccuracy: false,
};

export function loadDraft(): Partial<FormState> | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<FormState>;
  } catch {
    return null;
  }
}

export function buildInitialFormState(user: AuthUserInfo | null): FormState {
  const fullName = getContactName(user);
  const draft = loadDraft();
  const base: FormState = draft ? { ...EMPTY_FORM, ...draft } : { ...EMPTY_FORM };
  // Country and dial code are fixed to Ethiopia and are not user-editable, so
  // never let a stale draft (e.g. saved before this restriction) blank them.
  base.country = 'Ethiopia';
  base.phoneDial = '+251';
  if (!base.contactPerson.trim() && fullName) {
    base.contactPerson = fullName;
  }
  return base;
}

export { getContactName };

export function buildDraftPayload(form: FormState): Partial<FormState> {
  const { acknowledgeAccuracy: _a, logo: _logo, logoPreview: _preview, logoUrl: _url, ...rest } = form;
  void _a;
  void _logo;
  void _preview;
  void _url;
  return rest;
}

export function validateStep(step: number, f: FormState): StepErrors {
  const e: StepErrors = {};
  if (step === 1) {
    if (f.orgName.trim().length < 3) {
      e.orgName = "Enter the organization's registered name (at least 3 characters).";
    }
    if (!f.sector.trim()) e.sector = 'Select the sector that best represents your institution.';
    if (!f.organizationSize.trim()) e.organizationSize = 'Choose the size that matches your workforce.';
    if (f.website.trim() && !URL_PATTERN.test(f.website.trim())) {
      e.website = 'Use a complete URL beginning with http:// or https://.';
    }
  }
  if (step === 2) {
    if (!f.country.trim()) e.country = 'Select the country where the organization is headquartered.';
    if (!f.city.trim()) e.city = 'Enter the city of your headquarters.';
    if (f.address.trim().length < 6) e.address = 'Provide a complete physical address.';
    if (!PHONE_PATTERN.test(f.phoneNumber.trim())) e.phoneNumber = 'Enter a valid contact phone number.';
    if (f.contactPerson.trim().length < 2) e.contactPerson = 'Enter the full name of the primary contact.';
    if (f.contactRole.trim().length < 2) e.contactRole = 'Enter the job position of the contact.';

    const hasAlternateName = f.alternateContactName.trim().length > 0;
    const hasAlternateRole = f.alternateContactRole.trim().length > 0;
    const hasAlternateEmail = f.alternateContactEmail.trim().length > 0;

    if (hasAlternateName || hasAlternateRole || hasAlternateEmail) {
      if (hasAlternateName && f.alternateContactName.trim().length < 2) {
        e.alternateContactName = 'Enter a valid name for the alternate contact.';
      }
      if (hasAlternateEmail && !EMAIL_PATTERN.test(f.alternateContactEmail.trim())) {
        e.alternateContactEmail = 'Enter a valid email for the alternate contact.';
      }
    }

    const len = f.description.trim().length;
    if (len > 0 && len < DESCRIPTION_MIN) {
      e.description = `Description must be at least ${DESCRIPTION_MIN} characters if provided.`;
    } else if (len > DESCRIPTION_MAX) {
      e.description = `Description must not exceed ${DESCRIPTION_MAX} characters.`;
    }
  }
  if (step === 3) {
    if (!f.acknowledgeAccuracy) {
      e.acknowledgeAccuracy = 'Please confirm the information you have submitted is accurate.';
    }
  }
  return e;
}

export function buildRegistrationPayload(form: FormState): RegisterOrganizationPayload {
  const slug = slugify(form.orgName.trim());
  const phone = `${form.phoneDial} ${form.phoneNumber.trim()}`.trim();

  return {
    orgName: form.orgName.trim(),
    orgSlug: slug,
    sector: form.sector.trim(),
    industry: form.industry.trim() || undefined,
    description: form.description.trim() || undefined,
    website: form.website.trim() || undefined,
    country: form.country.trim(),
    city: form.city.trim(),
    address: form.address.trim(),
    phone,
    organizationSize: form.organizationSize as RegisterOrganizationPayload['organizationSize'],
    logo: form.logoUrl || undefined,
    contactPerson: form.contactPerson.trim(),
    contactRole: form.contactRole.trim(),
    alternateContactName: form.alternateContactName.trim() || undefined,
    alternateContactRole: form.alternateContactRole.trim() || undefined,
    alternateContactEmail: form.alternateContactEmail.trim() || undefined,
  };
}

function getContactName(user: AuthUserInfo | null): string {
  if (!user) return '';
  if (user.givenName && user.familyName) {
    return `${user.givenName} ${user.familyName}`.trim();
  }
  if (user.name && user.name.trim()) return user.name.trim();
  if (user.givenName && user.givenName.trim()) return user.givenName.trim();
  if (user.familyName && user.familyName.trim()) return user.familyName.trim();
  if ((user as any).preferred_username && (user as any).preferred_username.trim()) {
    return (user as any).preferred_username.trim();
  }
  if (user.email && user.email.includes('@')) {
    const local = user.email.split('@')[0];
    return local
      .replace(/[._-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return '';
}
